import { BlueprintApiItem, BlueprintFamilyNewGame, BlueprintLiveEvent } from '../../../../api/core'
import { AppType, GamePlatform } from '../../../game/types/Game'
import { gameIconUrlOf, gameLevelBlueprintDisplayName } from './gameBlueprintDisplay'

export type NewAdopterGame = {
  gameId: string
  name: string
  iconUrl?: string
  // Publisher, when a lookup could supply one (the backend's `newGames` doesn't send it). Shown in the
  // unlock dialog of a locked game.
  artist?: string
  // Platform of the game (mobile vs PC/console), for the badge on its icon. Like `artist`, joined in from a
  // lookup: the backend's `newGames` doesn't send it.
  appType?: AppType
  platforms?: GamePlatform[]
  // First tracked event of the entity in this game.
  firstEvent: number
  // The game-level blueprint that first event belongs to, when known — what the
  // modal resolves the event itself from to deep-link into the calendar.
  firstBlueprintId?: string
  // Outside the user's plan (`gameAllowed: false`): listed, but its events won't open.
  locked?: boolean
}

/** The letter an avatar shows when a game has no icon: first code point, so emoji stay whole; `?` for an empty name. */
export const gameInitial = (name: string): string => Array.from(name.trim())[0]?.toUpperCase() ?? '?'

type ToNewAdopterGamesArgs = {
  userLanguage: string
  // Game id → icon URL, joined in from whatever the page already fetched (the
  // family page's `/blueprints` payload, the tracked-games list, …). Only used
  // for entries that arrive without icons of their own (backends predating
  // GR-3245); a game missing from both falls back to the letter avatar the tile
  // renders for an absent `iconUrl`.
  iconByGameId?: Record<string, string>
  // Game id → publisher, joined in the same way as the icons. A game missing from it simply has none.
  artistByGameId?: Record<string, string>
  // Game id → platform, joined in the same way: what the icon's mobile / PC-console badge is drawn from.
  appTypeByGameId?: Record<string, AppType>
  platformsByGameId?: Record<string, GamePlatform[]>
  // The family's game-level blueprints (`/blueprints`), to name the one each game
  // first adopted with. Only the blueprint-family page has them.
  gameBlueprints?: BlueprintApiItem[]
}

const closestTo = <T>(items: readonly T[], target: number, timestampOf: (item: T) => number | undefined): T | undefined => {
  let best: T | undefined
  let bestDistance = Infinity
  for (const item of items) {
    const timestamp = timestampOf(item)
    if (timestamp === undefined) continue
    const distance = Math.abs(timestamp - target)
    if (distance < bestDistance) {
      best = item
      bestDistance = distance
    }
  }
  return best
}

/**
 * The game-level blueprint a game first ran the family with: of the game's
 * implementations, the one whose earliest event sits closest to its first-event date.
 * A blueprint with no run (`earliestEvent` 0) can't be it.
 */
export function firstAdoptionBlueprintId(gameBlueprints: BlueprintApiItem[] | undefined, gameId: string, firstEvent: number): string | undefined {
  const own = (gameBlueprints ?? []).filter((item) => (item.gameId ?? item.id) === gameId)
  return closestTo(own, firstEvent, (item) => item.earliestEvent || undefined)?.id
}

/** The event of a game-level blueprint that started closest to `firstEvent` — the adopting event. */
export function firstAdoptionEvent(liveEvents: BlueprintLiveEvent[] | undefined, firstEvent: number): BlueprintLiveEvent | undefined {
  return closestTo(liveEvents ?? [], firstEvent, (event) => (event.eventId ? event.startDate : undefined))
}

/**
 * The games behind a New Adopters count: those whose FIRST-EVER event of the
 * entity falls inside the window, newest first.
 *
 * Read straight off the backend's `newGames` — the same cohort-scoped
 * population the count itself is built from, so the faces and the number can
 * no longer disagree. This replaced a client-side derivation over the
 * `/blueprints` payload's `earliestEvent`, which only the blueprint-family page
 * could run and which scoped games by subgenre by hand.
 *
 * Note a coarser entity can legitimately list FEWER new adopters than one of
 * its children: "first event of this event type" is a higher bar than "first
 * event of this blueprint family".
 */
export function toNewAdopterGames(
  newGames: BlueprintFamilyNewGame[] | undefined,
  { userLanguage, iconByGameId, artistByGameId, appTypeByGameId, platformsByGameId, gameBlueprints }: ToNewAdopterGamesArgs
): NewAdopterGame[] {
  if (!newGames || newGames.length === 0) return []

  // A game is one adopter however many times the payload lists it; its earliest first event wins, in the place
  // of its first listing so the backend's newest-first order holds.
  const earliestByGameId = new Map<string, BlueprintFamilyNewGame>()
  for (const game of newGames) {
    const seen = earliestByGameId.get(game.gameId)
    if (!seen || game.firstEventDate < seen.firstEventDate) earliestByGameId.set(game.gameId, game)
  }

  return Array.from(earliestByGameId.values()).map((game) => ({
    gameId: game.gameId,
    name: gameLevelBlueprintDisplayName(game.gameNames, userLanguage),
    iconUrl: gameIconUrlOf(game) ?? iconByGameId?.[game.gameId],
    artist: artistByGameId?.[game.gameId],
    appType: appTypeByGameId?.[game.gameId],
    platforms: platformsByGameId?.[game.gameId],
    firstEvent: game.firstEventDate,
    firstBlueprintId: firstAdoptionBlueprintId(gameBlueprints, game.gameId, game.firstEventDate),
    locked: game.gameAllowed === false,
  }))
}
